import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  IconButton,
  MenuItem,
  TextField,
  Switch,
  Typography,
  Box,
} from "@mui/material";
import { CloseOutlined, EditOutlined } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import DeliveryAddressList from "./DeliveryAddressList";
import ForwardAddressList from "./ForwardAddressList";
import { playAlertSound, playCorrectSound, playIncorrectSound } from "utils";
import { cleanLocalNumber } from "utils/phoneValidation";
import Swal from "sweetalert2";
import { QueryProvinces } from "api/general";
import { GetRouteList } from "api/routes";
import { PatchCustomer } from "api/customers";
import { GetAgentList } from "api/agent";
import { getAllCustomerGroups } from "api/customerGroup";
import { sectionLabelSx, fieldSx, accentFieldSx, switchSx, ToggleCard } from "components/bento/BentoFormControls";
import {
  BG_TINT,
  BORDER_PURPLE,
  BG_PURPLE_GRADIENT,
  RADIUS_CARD,
  DIVIDER,
  PURPLE_MAIN,
  PURPLE_DARK,
  TEXT_DARK,
  TEXT_BODY,
  MONO,
} from "components/bento/bentoTokens";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomerEditDialog = ({
  open,
  customerData,
  onClose,
  rows,
  setRows,
}) => {
  const { t } = useTranslation();
  const [customerId, setCustomerId] = useState(customerData?.id || "");
  const [name, setName] = useState(customerData?.name || "");
  const [whatsapp, setWhatsapp] = useState(customerData?.whatsapp || "");
  const [provinceId, setProvinceId] = useState(
    customerData?.province?.pr_id || ""
  );

  const [remark, setRemark] = useState(customerData?.remark || "");
  const [validflag, setValidflag] = useState(customerData?.validflag || true);
  const [routeId, setRouteId] = useState(customerData?.default_route || "");
  const [isCOD, setIsCOD] = useState(customerData?.isCOD || false);
  const [isDeliveryRequired, setIsDeliveryRequired] = useState(
    customerData?.isDeliveryRequired || false
  );
  const [isSubCustomer, setIsSubCustomer] = useState(
    customerData?.isSubCustomer || false
  );
  const [agentId, setAgentId] = useState(customerData?.agentId || "");
  const [isForwardRequired, setIsForwardRequired] = useState(
    customerData?.isForwardRequired || false
  );
  const [isSelfOrder, setIsSelfOrder] = useState(
    customerData?.isSelfOrder || false
  );

  const [deliveryAddresses, setDeliveryAddresses] = useState(
    customerData?.deliveryAddresses || []
  );
  const [forwardAddresses, setForwardAddresses] = useState(
    customerData?.forwardAddresses || []
  );
  const [customerGroups, setCustomerGroups] = useState([]);
  const [groupId, setGroupId] = useState(customerData?.groupId || "");

  const [level, setLevel] = useState(1);
  // Hand-over photos staff must attach when collecting this customer's parcels.
  const [requiredProofPhotos, setRequiredProofPhotos] = useState(customerData?.requiredProofPhotos || 2);
  // Preferred language — picks the WhatsApp pickup-reminder template and push
  // banner language. "" = not set (Lao reminder template is used).
  const [language, setLanguage] = useState(customerData?.language || "");

  const [provinces, setProvinces] = useState([]);
  const [routeList, setRouteList] = useState([]);
  const [agentList, setAgentList] = useState([]);

  useEffect(() => {
    if (customerData) {
      // Important: Check if customerData exists before setting state
      setCustomerId(customerData._id);
      setName(customerData.name);
      setWhatsapp(customerData.whatsapp);
      setProvinceId(customerData.province?.pr_id);
      setRemark(customerData.remark || "");
      setValidflag(customerData.validflag);
      setRouteId(customerData.default_route);
      setIsCOD(customerData.isCOD);
      setIsDeliveryRequired(customerData.isDeliveryRequired);
      setIsForwardRequired(customerData.isForwardRequired);
      setIsSubCustomer(customerData.isSubCustomer);
      setAgentId(customerData.agentId);
      setIsSelfOrder(customerData.isSelfOrder);
      setRequiredProofPhotos(customerData.requiredProofPhotos || 2);
      setLanguage(customerData.language || "");
      setDeliveryAddresses(customerData?.deliveryAddresses || []);
      setForwardAddresses(customerData?.forwardAddresses || []);
      setGroupId(customerData?.groupId || "");
    }
  }, [customerData]);

  useEffect(() => {
    QueryProvinces().then((res) => setProvinces(res.data));
    GetRouteList().then((res) => setRouteList(res.data));
    // Load customer groups
    getAllCustomerGroups().then((result) => {
      if (result.success) {
        const activeGroups = result.data.filter(group => group.isActive);
        setCustomerGroups(activeGroups);
      }
    });
    setLevel(1);
  }, []);
  useEffect(() => {
    GetAgentList().then((res) => {
      if (res && res.success) {
        setAgentList(res.data);
      }
    });
  }, [isSubCustomer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const provinceObj = provinces.find(
      (item) => item.pr_id === provinceId.toString()
    );

    if (!groupId) {
      playAlertSound();
      Swal.fire({
        title: t("newCustomer.customerGroupRequiredTitle"),
        icon: "warning",
        text: t("newCustomer.customerGroupRequiredText"),
      });
      return;
    }

    if (isDeliveryRequired && deliveryAddresses.length <= 0) {
      playAlertSound();
      Swal.fire({
        title: "Location Required!",
        icon: "warning",
        text: "ກະລຸນາເພີ່ມແຜນທີ່ໃນການຈັດສົ່ງສິນຄ້າ",
      });
      return;
    }

    if (isForwardRequired && forwardAddresses.length <= 0) {
      playAlertSound();
      Swal.fire({
        title: "Forward Address Required!",
        icon: "warning",
        text: "ກະລຸນາເພີ່ມທີ່ຢູ່ໃນການຈັດສົ່ງສິນຄ້າ",
      });
      return;
    }

    // Create FormData for the patch request
    const formData = new FormData();
    formData.append("_id", customerId);
    formData.append("name", name);
    formData.append("whatsapp", whatsapp);
    formData.append("groupId", groupId);
    formData.append("province", JSON.stringify(provinceObj));
    formData.append("validflag", validflag);
    formData.append("remark", remark);
    formData.append("default_route", routeId);
    formData.append("isCOD", isCOD);
    formData.append("isDeliveryRequired", isDeliveryRequired);
    formData.append("isForwardRequired", isForwardRequired);
    formData.append("isSubCustomer", isSubCustomer);
    formData.append("agentId", agentId);
    formData.append("level", level);
    formData.append("isSelfOrder", isSelfOrder);
    formData.append("requiredProofPhotos", requiredProofPhotos);
    formData.append("language", language);
    formData.append("deliveryAddresses", JSON.stringify(deliveryAddresses));
    formData.append("forwardAddresses", JSON.stringify(forwardAddresses));

    PatchCustomer(formData).then((res) => {
      if (res && res.success) {
        const updatedRows = rows.map((row) =>
          row._id === customerId
            ? {
                ...row,
                name,
                whatsapp,
                province: provinceObj,
                validflag,
                remark,
                default_route: routeId,
                isCOD,
                isDeliveryRequired,
                isForwardRequired,
                isSubCustomer,
                agentId,
                level,
                isSelfOrder,
                requiredProofPhotos,
                language: language || undefined,
                deliveryAddresses,
                forwardAddresses,
                groupId,
              }
            : row
        );
        setRows(updatedRows);

        playCorrectSound();
        Swal.fire({
          title: "Updated!",
          icon: "success",
          text: res.message,
          timer: 1500,
        });
        onClose();
      } else if (res.status === 400) {
        playIncorrectSound();
        Swal.fire({
          title: "Error!",
          icon: "error",
          text: `Status ${res.status} - ${res.data.message}`,
        });
      } else {
        playIncorrectSound();
        Swal.fire({
          title: "Error!",
          icon: "error",
          text: res.message,
        });
      }
    });
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth <= 600} // Adjust breakpoint as needed
      keepMounted
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{ sx: { borderRadius: RADIUS_CARD, overflow: "hidden" } }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ p: "24px 28px 20px" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: "12px", flexShrink: 0,
                  background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <EditOutlined sx={{ fontSize: 19, color: PURPLE_MAIN }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "20px", fontWeight: 800, color: TEXT_DARK, letterSpacing: "-.01em" }}>
                  Edit Customer
                </Typography>
                {name && (
                  <Typography sx={{ fontFamily: MONO, fontSize: "13px", fontWeight: 600, color: PURPLE_DARK, mt: "2px" }}>
                    {name}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton aria-label="close" onClick={onClose}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflowY: "auto", maxHeight: "calc(100vh - 220px)" }}>

          {/* Section 1: Customer Info */}
          <Box sx={{ p: "4px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
            <Box sx={sectionLabelSx}>Customer Info</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
              <TextField
                label="Name"
                id="name"
                value={name}
                required
                helperText="Customer name."
                sx={fieldSx}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                type="number"
                label="Whatsapp"
                id="whatsapp"
                value={whatsapp}
                required
                helperText="Whatsapp number example: 8562077790708."
                sx={fieldSx}
                onChange={(e) => setWhatsapp(cleanLocalNumber(e.target.value))}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
              <TextField
                select
                label="Customer Group / ກຸ່ມລູກຄ້າ"
                id="customerGroup"
                value={groupId}
                required
                sx={fieldSx}
                onChange={(event) => setGroupId(event.target.value)}
              >
                {customerGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name} - {group.whatsappAPI}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Province / ແຂວງ"
                id="province"
                value={!provinceId ? "" : provinceId}
                required
                sx={fieldSx}
                onChange={(event) => setProvinceId(event.target.value)}
              >
                {provinces &&
                  provinces.map(({ pr_id, pr_name, pr_name_en }) => (
                    <MenuItem key={pr_id} value={pr_id}>
                      {pr_name} / {pr_name_en}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>

            <TextField
              select
              fullWidth
              label="Route"
              id="route"
              value={!routeId ? "" : routeId}
              required
              sx={{ ...fieldSx, mb: "16px" }}
              onChange={(event) => setRouteId(event.target.value)}
            >
              {routeList &&
                routeList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {id} - {name}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              maxRows={5}
              label="Remark / ໝາຍເຫດ"
              id="remark"
              value={remark}
              sx={{ ...fieldSx, mb: "16px" }}
              onChange={(e) => setRemark(e.target.value)}
            />

            <TextField
              select
              fullWidth
              label={t("newCustomer.languageLabel")}
              id="language"
              value={language}
              helperText={t("newCustomer.languageHelp")}
              sx={fieldSx}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <MenuItem value="">{t("newCustomer.languageNotSet")}</MenuItem>
              <MenuItem value="lo">{t("newCustomer.languageLo")}</MenuItem>
              <MenuItem value="th">{t("newCustomer.languageTh")}</MenuItem>
              <MenuItem value="en">{t("newCustomer.languageEn")}</MenuItem>
            </TextField>
          </Box>

          {/* Section 2: Delivery Options */}
          <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
            <Box sx={sectionLabelSx}>Delivery Options</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "12px", mb: "12px" }}>
              <ToggleCard
                label="COD ເກັບຄ່າບໍລິການປາຍທາງ"
                checked={isCOD}
                onChange={() => setIsCOD(!isCOD)}
              />
              <ToggleCard
                label="ລູກຄ້າສັ່ງເຄື່ອງເອງ"
                checked={isSelfOrder}
                onChange={() => setIsSelfOrder(!isSelfOrder)}
              />
            </Box>

            <TextField
              select
              fullWidth
              label="Hand-over photos required / ຈຳນວນຮູບຫຼັກຖານຕອນສົ່ງມອບ"
              id="requiredProofPhotos"
              value={requiredProofPhotos}
              sx={{ ...fieldSx, mb: "12px" }}
              onChange={(e) => setRequiredProofPhotos(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <ToggleCard
                label="Home Delivery ຈັດສົ່ງສິນຄ້າຮອດເຮືອນລູກຄ້າ ພາຍໃນຂອບເຂດຕົວເມືອງ"
                checked={isDeliveryRequired}
                onChange={() => setIsDeliveryRequired(!isDeliveryRequired)}
              >
                {isDeliveryRequired && (
                  <DeliveryAddressList
                    deliveryAddresses={deliveryAddresses}
                    setDeliveryAddresses={setDeliveryAddresses}
                    isDeliveryRequired={isDeliveryRequired}
                  />
                )}
              </ToggleCard>

              <ToggleCard
                label="ຕ້ອງໄດ້ຝາກນຳຂົນສົ່ງພາຍໃນປະເທດຕໍ່ ເຊັ່ນ​ ຮຸ່ງອາລຸນ, ອະນຸສິດ, ມີໄຊ ..."
                checked={isForwardRequired}
                onChange={() => setIsForwardRequired(!isForwardRequired)}
              >
                {isForwardRequired && (
                  <ForwardAddressList
                    forwardAddresses={forwardAddresses}
                    setForwardAddresses={setForwardAddresses}
                    isForwardRequired={isForwardRequired}
                  />
                )}
              </ToggleCard>
            </Box>
          </Box>

          {/* Section 3: Agent */}
          <Box sx={{ p: "22px 28px 24px" }}>
            <Box sx={sectionLabelSx}>Agent</Box>
            <ToggleCard
              label="ລູກຄ້າຂອງຕົວແທນ"
              checked={isSubCustomer}
              onChange={() => setIsSubCustomer(!isSubCustomer)}
            >
              {isSubCustomer && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="ເລືອກຕົວແທນ Agent"
                  id="agent"
                  value={!agentId ? "" : agentId}
                  required
                  sx={accentFieldSx}
                  onChange={(event) => setAgentId(event.target.value)}
                >
                  {agentList &&
                    agentList.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {id} - {name}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </ToggleCard>
          </Box>

        </DialogContent>

        <DialogActions sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "20px 28px", background: BG_TINT }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: TEXT_BODY }}>
              {validflag ? "ເປີດໃຊ້ງານ" : "ປີດໃຊ້ງານ"}
            </Box>
            <Switch
              checked={validflag}
              onChange={() => setValidflag(!validflag)}
              sx={switchSx}
            />
          </Box>
          <Button
            disabled={!provinceId}
            variant="contained"
            color="secondary"
            type="submit"
            startIcon={<CheckIcon fontSize="small" />}
            sx={{
              background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`,
              boxShadow: "0 4px 16px -4px rgba(109,40,217,.55)",
              "&:hover": { background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`, opacity: 0.92 },
            }}
          >
            Save changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CustomerEditDialog;
